<template>
    <ModeSwitcher ref="modeSwitcher" :main-mode-text="$t('create')" class="pt-4" @reset="formData = initData()">
        <template v-slot:main-mode>
            <v-data-table
                :headers="useHeaders"
                :items="availability"
                :loading="isLoading"
                :items-length="availability.length"
                density="compact"
                class="elevation-1 table-server"
                data-cy="availability-table"
            >
                <template v-slot:item.periods="{item}">
                    <div v-if="item.dateFrom || item.dateTo">
                        {{ item.dateFrom ? $filters.dateShortFormat(item.dateFrom) : '' }}
                        {{ item.dateFrom && item.dateTo && '-' }}
                        {{ item.dateTo ? $filters.dateShortFormat(item.dateTo) : '' }}
                    </div>
                    <div class="text-medium-emphasis">
                        <span v-for="(day, index) in item.weekdays" :key="day">
                            {{ `${weekdaysName[day - 1]}${item.weekdays.length - 1 !== index ? ',' : ''} ` }}
                        </span>
                    </div>
                </template>
                <template v-slot:item.action="{item}">
                    <v-btn
                        variant="text"
                        color="primary"
                        class="mr-4 text-caption"
                        :data-cy:="`delete-${item.id}`"
                        density="compact"
                        @click="showModal(item.id)"
                    >
                        {{ $t('delete') }}
                    </v-btn>
                </template>
            </v-data-table>
        </template>
        <template v-slot:additional-mode>
            <v-form>
                <div class="py-4">
                    <div class="d-flex align-center flex-wrap">
                        <daterange
                            :start-date-label="$t('date_from')"
                            :end-date-label="$t('date_to')"
                            :_start-date="formData.dateFrom"
                            :_end-date="formData.dateTo"
                            same-date
                            :single-line="false"
                            class="mx-1 flex-grow-0"
                            content-class="date-field flex-grow-0"
                            :hide-details="false"
                            @change-start-date="formData.dateFrom = $event"
                            @change-end-date="formData.dateTo = $event"
                        />
                        <div class="d-flex flex-wrap flex-sm-nowrap">
                            <v-checkbox
                                v-for="(dayName, dayNumber) in weekdaysName"
                                :key="dayNumber"
                                v-model="formData.weekdays"
                                :label="dayName"
                                :value="dayNumber"
                                class="mx-1 flex-shrink-0"
                                :data-cy="dayNumber"
                            />
                            <v-checkbox
                                :model-value="formData.weekdays.length === 7"
                                :indeterminate="formData.weekdays.length !== 7 && formData.weekdays.length !== 0"
                                :label="`${$t('all')}/${$t('none')}`"
                                class="text-capitalize mx-1 flex-shrink-0"
                                data-cy="all-days"
                                @update:model-value="toggleAllDays"
                            />
                        </div>
                    </div>
                    <div v-if="!formData.weekdays.length" class="text-error">{{ $t('week_day_error') }}</div>
                </div>
                <v-row no-gutters>
                    <v-btn
                        color="primary"
                        class="mr-4"
                        data-cy="save"
                        :disabled="isLoading || !formData.weekdays.length"
                        @click="save"
                    >
                        {{ $t('save') }}
                    </v-btn>
                    <v-btn data-cy="cancel" variant="text" :disabled="isLoading" @click="modeSwitcher.goToTable()">
                        {{ $t('cancel') }}
                    </v-btn>
                </v-row>
            </v-form>
        </template>
    </ModeSwitcher>
    <ModalWrapper v-model="isShowModal" scrollable>
        <template v-slot:title>{{ $t('confirmation') }}</template>
        <template v-slot:content>{{ $t('delete_record') }}</template>
        <template v-slot:btns>
            <v-btn color="primary" variant="flat" data-cy="delete" @click="deleteAvailability">
                {{ $t('delete') }}
            </v-btn>
        </template>
    </ModalWrapper>
</template>

<script setup lang="ts">
    import Daterange from '~/components/search/forms/Daterange.vue'

    const {contract} = defineProps<{
        contract: any
    }>()

    const {$api, $toast} = useNuxtApp()
    const $route = useRoute()
    const {t} = useI18n()

    const isLoading = ref<boolean>(false)
    const availability = ref([])
    const availabilityLoading = ref(true)
    const modeSwitcher = ref(null)
    const isShowModal = ref<boolean>(false)
    const deletedAvailability = ref<string>(null)

    const {locale} = useI18n()
    const weekdaysName = ref([])
    const weekdaysArray = [...Array(7).keys()]
    const setupWeekdays = () => {
        const weekdays = new Intl.DateTimeFormat(locale.value, {weekday: 'short'})
        weekdaysName.value = [...weekdaysArray].map(day => weekdays.format(new Date(Date.UTC(2021, 5, day))))
    }
    watch(locale, setupWeekdays, {immediate: true})
    function toggleAllDays() {
        if (formData.value.weekdays.length !== 7) {
            formData.value.weekdays = [...weekdaysArray]
        } else {
            formData.value.weekdays = []
        }
    }

    const formData = ref(initData())
    function initData() {
        return {
            dateFrom: null,
            dateTo: null,
            weekdays: [...weekdaysArray],
        }
    }

    onMounted(async () => {
        await loadAvailability()
    })

    async function loadAvailability(): Promise<void> {
        try {
            availabilityLoading.value = true
            availability.value = await $api.ownPackageContractsAvailability.get(
                contract.id || $route.params?.contractId
            )
        } catch (error) {
            availability.value = null
        } finally {
            availabilityLoading.value = false
        }
    }

    const useHeaders = computed(() => {
        return [
            {title: t('periods'), value: 'periods'},
            {title: '', value: 'action', minWidth: '120px', width: '10%'},
        ]
    })

    const save = async () => {
        const data = clone(formData.value)
        data.weekdays = data.weekdays.map(day => day + 1)
        clean(data)

        try {
            isLoading.value = true
            await $api.ownPackageContractsAvailability.post(contract.id || $route.params?.contractId, data)
            $toast.success(t('success_message.saved_successfully'))
            await loadAvailability()
            modeSwitcher.value.goToTable()
        } catch (e) {
            $toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const showModal = id => {
        isShowModal.value = true
        deletedAvailability.value = id
    }

    const deleteAvailability = async () => {
        try {
            await $api.ownPackageContractsAvailability.remove(
                contract.id || $route.params?.contractId,
                deletedAvailability.value
            )
            $toast.success(t('success_message.saved_successfully'))
            await loadAvailability()
        } catch (e) {
            $toast.error(t('error_message.save_error'))
        } finally {
            deletedAvailability.value = null
            isShowModal.value = false
        }
    }
</script>
